import React, { useEffect, useRef, useState } from 'react';
import { Modal, notification, Spin } from 'antd';
import { OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { formatFullName, hasPermission } from 'utils/utils';
import dayjs from 'dayjs';
import { User } from 'types/userTypes';
import { getRequest, patchRequest, postRequest } from 'api/apiClient';
import { VisitorType } from 'types/VisitorTypeTypes';
import { DATE_TIME_AM_PM_FORMAT, DATE_TIME_FORMAT } from 'constants/dates';
import STATUS from 'constants/status';
import { fetchVisits } from 'store/slices/visitsSlice';
import { FormFieldWorkflow, VisitData } from 'types/formFieldTypes';
import { OpTabs } from 'components/customAntd/DLS/OpTabs/OpTabs';
import { LoadingOutlined } from '@ant-design/icons';
import { fetchInvitationConfig, fetchInvitationCustomFields } from 'store/slices/visitorInvitationSlice';
import { VisitorWorkflowFields } from 'types/visitorWorkflowTypes';
import { searchVisitorsToday } from 'store/slices/visitorSearchSlice';
import { useTranslation } from 'react-i18next';
import { LocationWorkflow } from 'types/locationTypes';
import i18n from 'i18n/i18n';

interface ModalFormProps {
    open: boolean;
    onClose: () => void;
    setLoading: (loading: boolean) => void;
}

const VisitorsEditModal: React.FC<ModalFormProps> = ({ open, onClose, setLoading }) => {
    const { t } = useTranslation();

    const dispatch: AppDispatch = useDispatch();

    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const { selectedVisit } = useSelector((state: RootState) => state.visits);
    const { invitationConfig, invitationCustomFields } = useSelector((state: RootState) => state.visitorInvitation);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);

    const [hosts, setHosts] = useState<User[]>([]);
    const [isHostAvailable, setIsHostAvailable] = useState(false);
    const [visitorTypes, setVisitorTypes] = useState<VisitorType[]>([]);
    const [visitData, setVisitData] = useState<VisitData[]>([]);
    const [activeKey, setActiveKey] = useState<string>('details');
    const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
    const [locationDefaultWorkflow, setLocationDefaultWorkflow] = useState<LocationWorkflow>();
    const [locationVisitorTypes, setLocationVisitorTypes] = useState<VisitorType[]>([]);
    const [signInWorkflowFields, setSignInWorkflowFields] = useState<VisitorWorkflowFields>();
    const [customFieldsWorkflow, setCustomFieldsWorkflow] = useState<FormFieldWorkflow[]>([]);

    const hasAllVisitorsWrite = hasPermission(tokenScopeList, orgId, 'o', 'allvisitors:w');

    const isInitialLoad = useRef(true);

    const [form] = OpForm.useForm();
    const selectedVisitorTypeId = OpForm.useWatch("visitorType", form);

    useEffect(() => {
        if (open) {
            setActiveKey('details');
        }
    }, [open]);

    useEffect(() => {
        const fetchData = async () => {
            setIsModalLoading(true);
            try {
                // Parallel fetch for independent data
                const [locationDefaultWorkflow, locationVisitorTypes, locationVisitorTypeWorkflow, users, visitorTypes, visitData] = await Promise.all([
                    getRequest(`/orgs/${orgId}/sites/${selectedVisit?.site?.id}/workflows`),
                    getRequest(`/orgs/${orgId}/sites/${selectedVisit?.site?.id}/visitorType`),
                    getRequest(`/orgs/${orgId}/sites/${selectedVisit?.site.id}/visitorType/${selectedVisit?.visitorType.id}`),
                    getRequest(`/orgs/${orgId}/users`),
                    getRequest(`/orgs/${orgId}/visitorTypes`),
                    getRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitorData`)
                ]);

                setLocationDefaultWorkflow(locationDefaultWorkflow.data[0]);
                setLocationVisitorTypes(locationVisitorTypes.data);
                setVisitorTypes(visitorTypes.data);
                setVisitData(visitData.data);

                // Process users and hosts
                const filteredUsers = users.data.filter((user: User) => user.status === 1 && (user.hostExclude === 0 || user.hostExclude === null));

                const visitHost = selectedVisit?.host;
                if (visitHost?.userId && !filteredUsers.some((user: User) => user.id === visitHost.userId)) {
                    const hostFromVisit = {
                        id: visitHost.userId,
                        identity: {
                            firstName: visitHost.firstName,
                            middleName: null,
                            lastName: visitHost.lastName,
                            email: visitHost.email,
                        },
                    } as User;

                    const updatedHosts = [...filteredUsers, hostFromVisit];
                    const filteredHosts = hasAllVisitorsWrite
                        ? updatedHosts
                        : updatedHosts.filter(host => host.id === globalUserId);

                    setHosts(filteredHosts);
                    setIsHostAvailable(filteredHosts.length > 0);
                } else {
                    const filteredHosts = hasAllVisitorsWrite
                        ? filteredUsers
                        : filteredUsers.filter((host: User) => host.id === globalUserId);

                    setHosts(filteredHosts);
                    setIsHostAvailable(filteredUsers.length > 0);
                }

                // Get workflow ID
                const defaultWorkflowData = locationDefaultWorkflow?.data?.[0];
                const visitorTypeWorkflowData = locationVisitorTypeWorkflow?.data?.[0]?.workflow;

                const signInWorkflowId =
                    visitorTypeWorkflowData?.SignInWorkflowId !== -1
                        ? visitorTypeWorkflowData.SignInWorkflowId
                        : defaultWorkflowData?.SignInWorkflowId;

                // Parallel fetch for workflow fields and configs
                const workflowPromises = [
                    getRequest(`/orgs/${orgId}/visitorWorkflow/${signInWorkflowId}/fieldCustom`),
                    getRequest(`/orgs/${orgId}/visitorWorkflow/${signInWorkflowId}/fieldConfig`)
                ];

                const dispatchPromises = [];
                if (invitationConfig === null) {
                    dispatchPromises.push(dispatch(fetchInvitationConfig({ orgId })));
                }
                if (invitationCustomFields.length === 0) {
                    dispatchPromises.push(dispatch(fetchInvitationCustomFields({ orgId })));
                }

                const [customFieldsWorkflow, signInWorkflowConfigResponse] = await Promise.all([
                    ...workflowPromises,
                    ...dispatchPromises
                ]);

                setCustomFieldsWorkflow(customFieldsWorkflow.data);
                setSignInWorkflowFields(signInWorkflowConfigResponse.data[0]);
            } catch (error) {
                console.log("Failed to fetch data.");
            } finally {
                setIsModalLoading(false);
            }
        };
        fetchData();
    }, [orgId, selectedVisit?.id, dispatch, globalUserId, hasAllVisitorsWrite, invitationConfig, invitationCustomFields.length, selectedVisit?.site.id, selectedVisit?.host.userId, selectedVisit?.visitorType.id, selectedVisit?.host]);

    useEffect(() => {
        const handleVisitorTypeChange = async () => {
            // Return if datas are not ready
            if (!selectedVisitorTypeId || locationVisitorTypes.length === 0) return;

            // Return if it's initial load (fields are already set during initial data setting)
            if (isInitialLoad.current) {
                isInitialLoad.current = false;
                return;
            }

            const selectedVisitorType = locationVisitorTypes.find(v => v.id === selectedVisitorTypeId);

            const hasVisitorTypeSignInWorkflow = (locationVisitorTypes ?? []).some(
                (type) => type.workflow?.SignInWorkflowId && type.workflow.SignInWorkflowId !== -1
            );

            // Return if selected visitor type doesn't exist in location visitor types or any workflow didn't get allocated to any visitor type
            if (!selectedVisitorType || !hasVisitorTypeSignInWorkflow) return;

            const workflowId = selectedVisitorType.workflow?.SignInWorkflowId;
            const finalWorkflowId = workflowId !== -1
                ? workflowId
                : locationDefaultWorkflow?.SignInWorkflowId;

            if (!finalWorkflowId) return;

            try {
                // Parallel fetch for field config and custom fields
                const [fieldConfig, fieldCustom] = await Promise.all([
                    getRequest(`/orgs/${orgId}/visitorWorkflow/${finalWorkflowId}/fieldConfig`),
                    getRequest(`/orgs/${orgId}/visitorWorkflow/${finalWorkflowId}/fieldCustom`)
                ]);

                setSignInWorkflowFields(fieldConfig.data[0]);
                setCustomFieldsWorkflow(fieldCustom.data);

                if (hasVisitorTypeSignInWorkflow) {
                    notification.info({
                        message: t('notification.notification'),
                        description: t('signInManuallyDrawer.notification.changeFieldsNotification'),
                        placement: 'bottomRight',
                    });
                }

            } catch (error) {
                notification.error({
                    message: t('notification.error'),
                    description: t('signInManuallyDrawer.notification.changeFieldsError'),
                    placement: 'bottomRight',
                });
            }
        };
        handleVisitorTypeChange();

    }, [orgId, selectedVisitorTypeId, locationDefaultWorkflow, locationVisitorTypes, t]);

    const initialValues: { [key: string]: any } = {
        company: selectedVisit?.company,
        siteId: selectedVisit?.site?.id,
        visitorType: selectedVisit?.visitorType?.id,
        hostId: selectedVisit?.host?.userId,
        purpose: selectedVisit?.purpose,
        signIn: dayjs(selectedVisit?.signIn),
        signOut: dayjs(selectedVisit?.signOut),
        scheduleStart: dayjs(selectedVisit?.scheduleStart),
        scheduleEnd: dayjs(selectedVisit?.scheduleEnd),
    };

    // Add visitData fields to initialValues
    visitData.forEach((data) => {
        const { formField, value } = data;
        initialValues[formField.id] = value;
    });

    const handleFieldsChange = (changedFields: any) => {
        const changedField = changedFields[0];
        if (changedField) {
            const { name, value } = changedField;
            const [field] = name;

            if (field === 'scheduleStart') {
                const scheduleStart = value;
                if (scheduleStart) {
                    const startMoment = dayjs(scheduleStart);
                    const newEndTime = startMoment.add(1, 'hour');
                    form.setFieldsValue({ scheduleEnd: newEndTime });
                }
            } else if (field === 'scheduleEnd') {
                const scheduleEnd = value;
                const scheduleStart = form.getFieldValue('scheduleStart');

                if (scheduleStart && scheduleEnd) {
                    const startMoment = dayjs(scheduleStart);
                    const endMoment = dayjs(scheduleEnd);

                    if (startMoment.isSameOrAfter(endMoment)) {
                        form.setFieldsValue({ scheduleStart: endMoment.subtract(1, 'hour') });
                    }
                }
            }
        }
    };

    const editVisit = async (updatedVisit: any) => {
        if (updatedVisit.scheduleStart && updatedVisit.scheduleEnd && updatedVisit.scheduleEnd.isSameOrBefore(updatedVisit.scheduleStart)) {
            notification.error({
                message: t('notification.error'),
                description: t('visitors.header.editModal.notification.invalidDateRangeError'),
                placement: 'bottomRight',
            });
            return;
        }

        if (updatedVisit.signIn && updatedVisit.signOut && updatedVisit.signOut.isSameOrBefore(updatedVisit.signIn)) {
            notification.error({
                message: t('notification.error'),
                description: t('visitors.header.editModal.notification.invalidDateRangeError'),
                placement: 'bottomRight',
            });
            return;
        }

        setLoading(true);


        try {
            const filteredUpdatedVisit = Object.fromEntries(
                Object.entries(updatedVisit).filter(([key]) => isNaN(Number(key)))
            );

            await patchRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}?language=${i18n.language.split('-')[0]}`, {
                visitorType: filteredUpdatedVisit.visitorType,
                company: filteredUpdatedVisit.company,
                hostId: filteredUpdatedVisit.hostId ?? -1,
                purpose: filteredUpdatedVisit.purpose,
                signIn: updatedVisit.signIn ? dayjs(updatedVisit.signIn).format(DATE_TIME_FORMAT) : undefined,
                signOut: updatedVisit.signOut ? dayjs(updatedVisit.signOut).format(DATE_TIME_FORMAT) : undefined,
                scheduleIn: updatedVisit.scheduleStart?.format(DATE_TIME_FORMAT),
                scheduleOut: updatedVisit.scheduleEnd?.format(DATE_TIME_FORMAT)
            });

            for (const key in updatedVisit) {
                const field = updatedVisit[key];
                const initialField = initialValues[key];

                // Check if the field is a custom field (custom fields have numeric keys)
                const isCustomField = !isNaN(Number(key));
                if (isCustomField && field !== initialField) {
                    // Add field if enabled
                    if (field && !initialField && !visitData.find(item => item.formField.id === Number(key))) {
                        try {
                            await postRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitorData`, {
                                fieldId: key,
                                value: field,
                            });
                            console.log(`Added custom field ${key}.`);
                        } catch (error) {
                            console.log(`Failed to add custom field ${key}:`, error);
                        }
                    } else {
                        try {
                            const dataId = visitData.find((data) => Number(data.formField.id) === Number(key))?.id;
                            await patchRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitorData/${dataId}`, {
                                fieldId: key,
                                value: field,
                            });
                            console.log(`Updated custom field ${key}.`);
                        } catch (error) {
                            console.log(`Failed to update custom field ${key}:`, error);
                        }
                    }
                }
            }
            await dispatch(fetchVisits({ orgId }));
            await dispatch(searchVisitorsToday({ orgId }));

            notification.success({
                message: t('notification.success'),
                description: t('visitors.header.editModal.notification.updateSuccess'),
                placement: 'bottomRight',
            });
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('visitors.header.editModal.notification.updateError'),
                placement: 'bottomRight',
            });
        } finally {
            setLoading(false);
        }
    };

    const renderFormFields = () => {

        const isPendingOrNoShow =
            selectedVisit?.visitStatus.id === STATUS.PENDING.id

        const fieldsToRender = isPendingOrNoShow
            ? Array.from(
                new Map(
                    [...customFieldsWorkflow, ...invitationCustomFields].map(f => [f.id, f])
                ).values()
            )
            : customFieldsWorkflow;

        const customFieldIds = fieldsToRender.map(field => field.id);
        // const isPending = selectedVisit?.visitStatus.id === STATUS.PENDING.id;

        return (
            <>
                {fieldsToRender.map((formField, index) => {
                    const rules = formField.required === 1 && !(isPendingOrNoShow && !invitationCustomFields.some(f => f.id === formField.id))
                        ? [{ required: true, message: t('visitors.header.editModal.form.customFieldRequired', { 'customFieldName': formField.name }) }]
                        : [];

                    if (formField.values) {
                        const options = formField.values.map((option) => ({
                            label: option.display || option.name,
                            value: option.value,
                        }));

                        return (
                            <OpForm.Select
                                key={formField.id}
                                label={formField.name}
                                name={formField.id}
                                options={options}
                                rules={rules}
                                placeholder={formField.placeholder ? formField.placeholder : undefined}
                                allowClear
                            />
                        );
                    } else {
                        return (
                            <OpForm.TextAreaInput
                                key={formField.id}
                                label={formField.name}
                                name={formField.id}
                                rows={4}
                                rules={rules}
                                placeholder={formField.placeholder ? formField.placeholder : undefined}
                            />
                        );
                    }
                })}

                {visitData
                    .filter(visitField => !customFieldIds.includes(visitField.formField.id)) // Fields not in customFieldsWorkflow
                    .map(visitField => {
                        const formField = visitField.formField;

                        if (formField.values) {
                            const options = formField.values.map((option) => ({
                                label: option.display || option.name,
                                value: option.value,
                            }));

                            return (
                                <OpForm.Select
                                    key={formField.id}
                                    label={formField.name}
                                    name={formField.id}
                                    options={options}
                                    placeholder={formField.placeholder ? formField.placeholder : undefined}
                                    allowClear
                                />
                            );
                        } else {
                            return (
                                <OpForm.Input
                                    key={formField.id}
                                    label={formField.name}
                                    name={formField.id}
                                    placeholder={formField.placeholder ? formField.placeholder : undefined}
                                />
                            );
                        }
                    })}
            </>
        );
    };

    // Define the tabs with the `items` prop
    const tabItems = [
        {
            key: 'details',
            label: t('visitors.header.editModal.detailsTab'),
            children: (
                <>
                    {selectedVisit?.visitStatus.id === STATUS.PENDING.id && (
                        <>
                            <OpForm.Select
                                label={t('visitors.header.editModal.form.visitorType')}
                                name="visitorType"
                                rules={[{ required: true, message: t('visitors.header.editModal.form.visitorTypeRequired') }]}
                                placeholder={t('visitors.header.editModal.form.visitorType')}
                                options={visitorTypes.map(type => ({
                                    label: t(type.languageCode ?? ''),
                                    value: type.id
                                }))}
                            />
                            <OpForm.DatePicker
                                format={DATE_TIME_AM_PM_FORMAT}
                                showTime
                                minuteStep={5}
                                name="scheduleStart"
                                label={t('visitors.header.editModal.form.scheduleStart')}
                                placeholder={t('visitors.header.editModal.form.selectDate')}
                                rules={[{ required: true, message: t('visitors.header.editModal.form.dateRequired') }]}
                            />
                            <OpForm.DatePicker
                                format={DATE_TIME_AM_PM_FORMAT}
                                showTime
                                minuteStep={5}
                                name="scheduleEnd"
                                label={t('visitors.header.editModal.form.scheduleEnd')}
                                placeholder={t('visitors.header.editModal.form.selectDate')}
                                rules={[{ required: true, message: t('visitors.header.editModal.form.dateRequired') }]}
                            />
                        </>
                    )}
                    {(selectedVisit?.visitStatus.id === STATUS.SIGNED_IN.id) && ( // Sign In time field is required when status is 'Signed In'
                        <OpForm.DatePicker
                            format={DATE_TIME_AM_PM_FORMAT}
                            showTime
                            minuteStep={5}
                            name="signIn"
                            label={t('visitors.header.editModal.form.signIn')}
                            placeholder={t('visitors.header.editModal.form.selectDate')}
                            rules={[
                                { required: true, message: t('visitors.header.editModal.form.dateRequired') },
                                {
                                    validator: (_, value) =>
                                        value && value.isAfter(dayjs()) // it cannot be in the future time
                                            ? Promise.reject(new Error(t('visitors.header.editModal.notification.invalidSignInTimeError')))
                                            : Promise.resolve(),
                                },
                            ]}
                        />
                    )}

                    {selectedVisit?.visitStatus.id === STATUS.SIGNED_OUT.id && (
                        <>
                            <OpForm.DatePicker
                                format={DATE_TIME_AM_PM_FORMAT}
                                showTime
                                minuteStep={5}
                                name="signIn"
                                label={t('visitors.header.editModal.form.signIn')}
                                placeholder={t('visitors.header.editModal.form.selectDate')}
                                rules={[
                                    { required: true, message: t('visitors.header.editModal.form.dateRequired') },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const signOut = getFieldValue('signOut');

                                            if (value && value.isAfter(dayjs())) { // it cannot be in the future time
                                                return Promise.reject(new Error(t('visitors.header.editModal.notification.invalidSignInTimeError')))
                                            }

                                            if (value && signOut && value.isSameOrAfter(signOut)) { // it cannot be after end date
                                                return Promise.reject(new Error(t('visitors.header.editModal.notification.startDateAfterEndDateError')));
                                            }
                                            return Promise.resolve();
                                        },

                                    })
                                ]}
                                onChange={() => form.validateFields(['signOut'])}
                            />
                            <OpForm.DatePicker
                                format={DATE_TIME_AM_PM_FORMAT}
                                showTime
                                minuteStep={5}
                                name="signOut"
                                label={t('visitors.header.editModal.form.signOut')}
                                placeholder={t('visitors.header.editModal.form.selectDate')}
                                rules={[
                                    { required: true, message: t('visitors.header.editModal.form.dateRequired') },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const signIn = getFieldValue('signIn');

                                            if (value && signIn && value.isSameOrBefore(signIn)) { // it cannot be before start date
                                                return Promise.reject(new Error(t('visitors.header.editModal.notification.invalidDateRangeError')));
                                            }
                                            return Promise.resolve();
                                        },

                                    })
                                ]}
                                onChange={() => form.validateFields(['signIn'])}
                            />
                        </>
                    )}

                    <OpForm.Input
                        label={t('visitors.header.editModal.form.company')}
                        name="company"
                        rules={[
                            {
                                required:
                                    (selectedVisit?.visitStatus.id === STATUS.PENDING.id && invitationConfig?.visitCompany.required === 1) ||
                                    ((selectedVisit?.visitStatus.id === STATUS.SIGNED_IN.id ||
                                        selectedVisit?.visitStatus.id === STATUS.SIGNED_OUT.id ||
                                        selectedVisit?.visitStatus.id === STATUS.DENIED_ENTRY.id) &&
                                        signInWorkflowFields?.visitCompany.required === 1),
                                message: t('visitors.header.editModal.form.companyRequired'),
                            }
                        ]}
                    />
                    <OpForm.Select
                        label={t('visitors.header.editModal.form.host')}
                        name="hostId"
                        showSearch
                        placeholder={isHostAvailable
                            ? t('visitors.header.editModal.form.hostPlaceholder')
                            : t('visitors.header.editModal.form.hostNoAvailable')}
                        disabled={!isHostAvailable}
                        filterOption={(input, option) => {
                            const label = option?.label;
                            return typeof label === 'string' && label.toLowerCase().includes(input.toLowerCase());
                        }}
                        rules={[
                            {
                                required:
                                    (selectedVisit?.visitStatus.id === STATUS.PENDING.id && invitationConfig?.host.required === 1) ||
                                    ((selectedVisit?.visitStatus.id === STATUS.SIGNED_IN.id ||
                                        selectedVisit?.visitStatus.id === STATUS.SIGNED_OUT.id ||
                                        selectedVisit?.visitStatus.id === STATUS.DENIED_ENTRY.id) &&
                                        signInWorkflowFields?.host.required === 1),
                                message: t('visitors.header.editModal.form.hostRequired'),
                            }
                        ]}
                        options={hosts.map(user => ({
                            label: user.identity.firstName || user.identity.lastName
                                ? formatFullName(user.identity.firstName, user.identity.middleName, user.identity.lastName)
                                : user.identity.email,
                            value: user.id
                        }))}
                        allowClear
                    />
                    <OpForm.TextAreaInput
                        rows={3}
                        label={t('visitors.header.editModal.form.purpose')}
                        name="purpose"
                        rules={[
                            {
                                required:
                                    (selectedVisit?.visitStatus.id === STATUS.PENDING.id && invitationConfig?.visitPurpose.required === 1) ||
                                    ((selectedVisit?.visitStatus.id === STATUS.SIGNED_IN.id ||
                                        selectedVisit?.visitStatus.id === STATUS.SIGNED_OUT.id ||
                                        selectedVisit?.visitStatus.id === STATUS.DENIED_ENTRY.id) &&
                                        signInWorkflowFields?.visitPurpose.required === 1),
                                message: t('visitors.header.editModal.form.purposeRequired'),
                            }
                        ]}
                    />
                </>
            )
        },
        ...(visitData.length > 0 ||
            ((selectedVisit?.visitStatus.id === STATUS.SIGNED_IN.id || selectedVisit?.visitStatus.id === STATUS.SIGNED_OUT.id || selectedVisit?.visitStatus.id === STATUS.DENIED_ENTRY.id) && customFieldsWorkflow.length > 0) ||
            ((selectedVisit?.visitStatus.id === STATUS.PENDING.id) && (invitationCustomFields.length > 0 || customFieldsWorkflow.length > 0))
            ? [{
                key: 'additionalInfo',
                label: t('visitors.header.editModal.additionalInfoTab'),
                children: renderFormFields(),
            }]
            : []
        )
    ];


    return (
        <Modal
            open={open}
            title={t('visitors.header.editModal.title')}
            okText={t('visitors.header.editModal.save')}
            cancelText={t('visitors.header.editModal.cancel')}
            onCancel={() => {
                form.resetFields();
                onClose();
            }}
            onOk={() => {
                form
                    .validateFields()
                    .then((values) => {
                        form.submit();
                        onClose();
                    })
                    .catch((errorInfo) => {
                        notification.error({
                            message: t('notification.error'),
                            description: t('notification.validationError'),
                            placement: 'bottomRight',
                        });
                    });
            }}
            centered
        >
            {isModalLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                </div>
            ) : (
                <div style={{ overflowY: 'auto', maxHeight: '63vh', padding: '6px' }}>
                    <OpForm
                        form={form}
                        initialValues={initialValues}
                        onFieldsChange={handleFieldsChange}
                        onSubmit={({ values }) => {
                            editVisit(values);
                            form.resetFields();
                        }}
                        hasError={false}
                        defaultButtons={false}
                    >
                        <OpTabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
                    </OpForm>
                </div>
            )}
        </Modal>
    );
};

export default VisitorsEditModal;
